import api, { API_BASE_URL } from '../api';

// ── Types ───────────────────────────────────────────────────────────────────

export type NewsLang = 'ru' | 'kk';
export type NewsStatus = 'draft' | 'published';

export interface NewsCategoryPublic {
  key: string;
  title_ru: string;
  title_kk: string;
  sort_order: number;
}

export interface NewsCategoryAdmin extends NewsCategoryPublic {
  id: string;
  post_count: number;
}

export interface NewsListItem {
  slug: string;
  title_ru: string;
  title_kk: string;
  excerpt_ru: string;
  excerpt_kk: string;
  cover_url: string | null;
  category_key: string | null;
  category_title_ru: string;
  category_title_kk: string;
  is_featured: boolean;
  published_at: string | null;
  reading_time: number;
  view_count: number;
}

export interface NewsListResponse {
  items: NewsListItem[];
  total: number;
}

export interface NewsDetailPublic {
  slug: string;
  lang: NewsLang;
  title: string;
  excerpt: string;
  body_html: string;
  cover_url: string | null;
  category_key: string | null;
  category_title: string;
  author_name: string;
  tags: string[];
  published_at: string | null;
  reading_time: number;
  view_count: number;
  seo_title: string;
  seo_description: string;
}

export interface NewsVersion {
  version: number;
  edited_by: string;
  edited_at: string;
}

export interface NewsAdminListItem {
  slug: string;
  title_ru: string;
  title_kk: string;
  category_key: string | null;
  status: NewsStatus;
  is_featured: boolean;
  published_at: string | null;
  cover_url: string | null;
  view_count: number;
  version: number;
  updated_by: string | null;
  updated_at: string | null;
  created_at: string;
}

export interface NewsAdminListResponse {
  items: NewsAdminListItem[];
  total: number;
}

export interface NewsAdminDetail extends NewsAdminListItem {
  excerpt_ru: string;
  excerpt_kk: string;
  body_ru: string;
  body_kk: string;
  cover_image_id: string | null;
  tags: string[];
  author_name: string;
  seo_title_ru: string;
  seo_title_kk: string;
  seo_description_ru: string;
  seo_description_kk: string;
  reading_time: number;
  history: NewsVersion[];
}

export interface NewsCreateBody {
  slug: string;
  title_ru: string;
  title_kk?: string;
  excerpt_ru?: string;
  excerpt_kk?: string;
  body_ru?: string;
  body_kk?: string;
  cover_image_id?: string | null;
  category_key?: string | null;
  tags?: string[];
  author_name?: string;
}

export interface NewsUpdateBody {
  title_ru?: string;
  title_kk?: string;
  excerpt_ru?: string;
  excerpt_kk?: string;
  body_ru?: string;
  body_kk?: string;
  cover_image_id?: string | null;
  category_key?: string | null;
  tags?: string[];
  author_name?: string;
  seo_title_ru?: string;
  seo_title_kk?: string;
  seo_description_ru?: string;
  seo_description_kk?: string;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

/** Build an absolute URL for a news image path/id returned by the backend. */
export const newsImageUrl = (pathOrId: string | null | undefined): string | undefined => {
  if (!pathOrId) return undefined;
  if (/^https?:\/\//i.test(pathOrId)) return pathOrId;
  if (pathOrId.startsWith('/')) return `${API_BASE_URL}${pathOrId}`;
  return `${API_BASE_URL}/news/images/${pathOrId}`;
};

// ── Public API ──────────────────────────────────────────────────────────────

export const listPublicNews = async (
  params: { category?: string; search?: string; skip?: number; limit?: number } = {}
): Promise<NewsListResponse> => {
  const { data } = await api.get<NewsListResponse>('/news', { params });
  return data;
};

export const getLatestNews = async (limit = 3): Promise<NewsListItem[]> => {
  const { data } = await api.get<NewsListItem[]>('/news/latest', { params: { limit } });
  return data;
};

export const getPublicNews = async (slug: string, lang: NewsLang): Promise<NewsDetailPublic> => {
  const { data } = await api.get<NewsDetailPublic>(`/news/${slug}`, { params: { lang } });
  return data;
};

export const listPublicCategories = async (): Promise<NewsCategoryPublic[]> => {
  const { data } = await api.get<NewsCategoryPublic[]>('/news/categories');
  return data;
};

// ── Admin API (superadmin only) ─────────────────────────────────────────────

export const listNews = async (
  params: { status?: NewsStatus; category?: string; search?: string; skip?: number; limit?: number } = {}
): Promise<NewsAdminListResponse> => {
  const { data } = await api.get<NewsAdminListResponse>('/admin/news', { params });
  return data;
};

export const getNews = async (slug: string): Promise<NewsAdminDetail> => {
  const { data } = await api.get<NewsAdminDetail>(`/admin/news/${slug}`);
  return data;
};

export const createNews = async (body: NewsCreateBody): Promise<NewsAdminDetail> => {
  const { data } = await api.post<NewsAdminDetail>('/admin/news', body);
  return data;
};

export const updateNews = async (slug: string, body: NewsUpdateBody): Promise<NewsAdminDetail> => {
  const { data } = await api.put<NewsAdminDetail>(`/admin/news/${slug}`, body);
  return data;
};

export const publishNews = async (slug: string, is_published: boolean): Promise<NewsAdminListItem> => {
  const { data } = await api.post<NewsAdminListItem>(`/admin/news/${slug}/publish`, { is_published });
  return data;
};

export const featureNews = async (slug: string, is_featured: boolean): Promise<NewsAdminListItem> => {
  const { data } = await api.post<NewsAdminListItem>(`/admin/news/${slug}/feature`, { is_featured });
  return data;
};

export const restoreNewsVersion = async (slug: string, version: number): Promise<NewsAdminDetail> => {
  const { data } = await api.post<NewsAdminDetail>(`/admin/news/${slug}/restore/${version}`);
  return data;
};

export const deleteNews = async (slug: string): Promise<void> => {
  await api.delete(`/admin/news/${slug}`);
};

export const uploadNewsImage = async (file: File): Promise<{ id: string; url: string }> => {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await api.post<{ id: string; url: string }>('/admin/news/images', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

// ── Admin categories ────────────────────────────────────────────────────────

export const listAdminCategories = async (): Promise<NewsCategoryAdmin[]> => {
  const { data } = await api.get<NewsCategoryAdmin[]>('/admin/news/categories');
  return data;
};

export const createCategory = async (
  body: { key: string; title_ru: string; title_kk?: string; sort_order?: number }
): Promise<NewsCategoryAdmin> => {
  const { data } = await api.post<NewsCategoryAdmin>('/admin/news/categories', body);
  return data;
};

export const updateCategory = async (
  id: string,
  body: { title_ru?: string; title_kk?: string; sort_order?: number }
): Promise<NewsCategoryAdmin> => {
  const { data } = await api.put<NewsCategoryAdmin>(`/admin/news/categories/${id}`, body);
  return data;
};

export const deleteCategory = async (id: string): Promise<void> => {
  await api.delete(`/admin/news/categories/${id}`);
};
